import {
	ReactElement,
	RefObject,
	useCallback,
	useEffect,
	useRef,
	useState
} from "react";
import { UserDetailsBox } from "client/Features/Users/UserDetailsBox/UserDetailsBox";
import { useContactDialog } from "client/Features/AddressBook/ContactDialog";
import {
	ContactSource,
	getWorkspaceContactDetails,
	getWorkspaceContactIdFromUserId,
	WorkspaceContactDetailsModel
} from "client/Api/Email/contacts";
import {
	AlertManager,
	useAlerts,
	useIsMounted,
	useIsTouchDevice
} from "client/Hooks";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { HttpRequestError } from "client/Api/httpClient";
import {
	BusyIndicator,
	DropOverMenu,
	NoResults,
	RawHtml
} from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import {
	isNil,
	isNotNil
} from "client/Misc/utils";
import { never } from "client_legacy/ndaw/errors";
import styles from "./RawHtmlWithHoverableMentions.module.less";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import { tryParseInt } from "client/Misc/parseUtils";

export type RawHtmlWithHoverableMentionsProps = {
	html: string;
};

export const RawHtmlWithHoverableMentions = (props: RawHtmlWithHoverableMentionsProps): ReactElement => {
	const {
		html
	} = props;

	const [isDetailsDropOverOpen, setIsDetailsDropOverOpen] = useState<boolean>(false);
	const [mentionTarget, setMentionTarget] = useState<MentionTarget | null>(null);
	const [contactDetails, setContactDetails] = useState<ContactDetailsState | null>(null);

	const contentRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);
	const [contactDialog, openContactDialog] = useContactDialog();
	const isTouchDevice: boolean = useIsTouchDevice();
	const getIsMounted: () => boolean = useIsMounted();
	const alertManager: AlertManager = useAlerts();

	const mentionTargetUserId: number | undefined = mentionTarget?.userId;

	const dropOverMentionTargetRef: RefObject<MentionTarget | null> = useRef<MentionTarget | null>(null);

	const showDetailsHandler = useCallback(
		(mentionTarget: MentionTarget): void => {
			if (isTouchDevice) {
				return;
			}

			setMentionTarget(mentionTarget);

			if (dropOverMentionTargetRef.current?.element !== mentionTarget.element) {
				setContactDetails(null);
			}

			dropOverMentionTargetRef.current = mentionTarget;
			setIsDetailsDropOverOpen(true);
		},
		[isTouchDevice]
	);

	const hideDetailsHandler = useCallback(
		(mentionTarget?: MentionTarget): void => {
			if (mentionTarget
				&& dropOverMentionTargetRef.current?.element !== mentionTarget.element
			) {
				return;
			}

			setIsDetailsDropOverOpen(false);
			setMentionTarget(null);
			dropOverMentionTargetRef.current = null;
		},
		[]
	);

	const [triggerMouseEnter, cancelMouseEnter] = useDelayedEventMap(
		showDetailsHandler,
		300
	);

	const [triggerMouseLeave, cancelMouseLeave] = useDelayedEventMap(
		hideDetailsHandler,
		100
	);

	const dropOverContainerMouseEnterHandler = useCallback(
		(): void => {
			// Native events for the anchors defined in effect fire later then the react event
			// because of this mouseleave event for anchor fires after the mouseenter event for the dropover
			// Because of this I delay enter event to restore correct order of events
			setTimeout(
				(): void => {
					if (!getIsMounted()) {
						return;
					}

					if (!dropOverMentionTargetRef.current) {
						return;
					}

					const mentionTarget: MentionTarget = dropOverMentionTargetRef.current;

					triggerMouseEnter(mentionTarget);
					cancelMouseLeave(mentionTarget.element);
				},
				1
			);
		},
		[
			cancelMouseLeave,
			getIsMounted,
			triggerMouseEnter
		]
	);

	const dropOverContainerMouseLeaveHandler = useCallback(
		(): void => {
			if (!dropOverMentionTargetRef.current) {
				return;
			}

			const mentionTarget: MentionTarget = dropOverMentionTargetRef.current;
			triggerMouseLeave(mentionTarget);
			cancelMouseEnter(mentionTarget.element);
		},
		[cancelMouseEnter, triggerMouseLeave]
	);

	useEffect(
		(): EffectWithDestructorResult => {
			async function fetchContactDetails(userId: number, signal: AbortSignal): Promise<void> {
				try {
					setContactDetails({ kind: "loading" });

					const details: WorkspaceContactDetailsModel = await getWorkspaceContactDetails(
						getWorkspaceContactIdFromUserId(userId),
						signal
					);

					setContactDetails({
						kind: "loaded",
						contactDetails: details
					});
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					if (e instanceof HttpRequestError && e.statusCode === 404) {
						setContactDetails({ kind: "deleted" });
						return;
					}

					setContactDetails(null);
					alertManager.showError(localize("_GeneralErrorMessage"));
					console.error("Failed to fetch contact details:", e);
				}
			}

			if (isNil(mentionTargetUserId) || !isDetailsDropOverOpen) {
				return;
			}

			const cancellation = new AbortController();
			fetchContactDetails(mentionTargetUserId, cancellation.signal);

			return () => cancellation.abort();
		},
		[
			alertManager,
			mentionTargetUserId,
			isDetailsDropOverOpen
		]
	);

	const moreInfoHandler = useCallback(
		(): void => {
			if (isNil(mentionTargetUserId)) {
				return;
			}

			openContactDialog({
				source: ContactSource.Workspace,
				initialView: {
					kind: "details",
					contactId: getWorkspaceContactIdFromUserId(mentionTargetUserId)
				}
			});
		},
		[mentionTargetUserId, openContactDialog]
	);

	useEffect(
		(): EffectWithDestructorResult => {
			hasImplicitDependencyOn(html);

			const contentContainer: HTMLDivElement | null = contentRef.current;

			if (!contentContainer) {
				return;
			}

			const mentionAnchors: NodeListOf<HTMLAnchorElement> = contentContainer.querySelectorAll(
				`a[data-mention="@"]`
			);

			const abortController = new AbortController();
			const { signal } = abortController;

			mentionAnchors.forEach((anchor: HTMLAnchorElement): void => {
				const userIdString: string | undefined = anchor.dataset["value"];
				const userIdParsing: TryResult<number> = tryParseInt(userIdString);

				if (!userIdParsing.wasSuccessful) {
					return;
				}

				const userId: number = userIdParsing.result;

				const mouseEnterHandler = (): void => {
					triggerMouseEnter(
						{
							element: anchor,
							userId
						},
						signal
					);
					cancelMouseLeave(anchor);
				};

				const mouseLeaveHandler = (): void => {
					triggerMouseLeave(
						{
							element: anchor,
							userId
						},
						signal
					);
					cancelMouseEnter(anchor);
				};

				anchor.addEventListener(
					"mouseenter",
					mouseEnterHandler,
					{ signal }
				);

				anchor.addEventListener(
					"mouseleave",
					mouseLeaveHandler,
					{ signal }
				);
			});

			return (): void => {
				abortController.abort();
			};
		},
		[
			html,
			cancelMouseEnter,
			cancelMouseLeave,
			triggerMouseEnter,
			triggerMouseLeave
		]
	);

	const renderDetailsDropOver = (
		targetElement: HTMLElement,
		contactDetails: ContactDetailsState
	): ReactElement => {
		switch (contactDetails.kind) {
			case "loading":
				return (
					<DropOverMenu
						target={targetElement}
						targetAlignment="bl"
						contentAlignment="tl"
						onClose={hideDetailsHandler}
						hasResponsiveDropOver={false}
						title=""
						trackingId="mention-hover__drop-over"
					>
						<div className={styles.noDataScreen}>
							<BusyIndicator />
						</div>
					</DropOverMenu>
				);
			case "deleted":
				return (
					<DropOverMenu
						target={targetElement}
						targetAlignment="bl"
						contentAlignment="tl"
						onClose={hideDetailsHandler}
						hasResponsiveDropOver={false}
						title={localize("_UserDoesNotExist")}
						trackingId="mention-hover__drop-over"
					>
						<div className={styles.noDataScreen}>
							<NoResults
								icon="user"
								title={localize("_UserDoesNotExist")}
								message={localize("_TheUserIsDeletedOrInactive")}
								height="fill"
							/>
						</div>
					</DropOverMenu>
				);
			case "loaded":
				return (
					<UserDetailsBox
						trackingId="mention-hover__drop-over"
						target={targetElement}
						userInfo={{
							kind: "contactDetails",
							contactDetails: contactDetails.contactDetails
						}}
						onClose={hideDetailsHandler}
						onMoreInfo={moreInfoHandler}
						targetAlignment="bl"
						contentAlignment="tl"
					/>
				);
			default:
				never(contactDetails);
		}
	};

	return (
		<>
			<div ref={contentRef}>
				<RawHtml html={html} />
			</div>
			<div
				onMouseEnter={dropOverContainerMouseEnterHandler}
				onMouseLeave={dropOverContainerMouseLeaveHandler}
			>
				{isDetailsDropOverOpen
					&& isNotNil(mentionTarget)
					&& isNotNil(contactDetails)
					&& renderDetailsDropOver(mentionTarget.element, contactDetails)
				}
			</div>
			{contactDialog}
		</>
	);
};

RawHtmlWithHoverableMentions.displayName = "RawHtmlWithHoverableMentions";

type ContactDetailsState =
	| {
		kind: "loading";
	}
	| {
		kind: "loaded";
		contactDetails: WorkspaceContactDetailsModel;
	}
	| {
		kind: "deleted";
	};

type MentionTarget = {
	element: HTMLAnchorElement;
	userId: number;
};

// NOTE this is a forked version of useDelayedEvent hook. Its needed because for this implementation
// we need to track delay states for various anchors
function useDelayedEventMap(
	callback: (mentionTarget: MentionTarget) => void,
	delay: number
): [
	trigger: (
		mentionTarget: MentionTarget,
		signal?: AbortSignal
	) => void,
	cancel: (anchor: HTMLAnchorElement) => void
] {
	const timeoutMapRef: RefObject<Map<HTMLAnchorElement, SetTimeoutHandle>> = useRef<Map<HTMLAnchorElement, SetTimeoutHandle>>(null!);
	if (!timeoutMapRef.current) {
		timeoutMapRef.current = new Map();
	}

	const trigger = useCallback(
		(mentionTarget: MentionTarget, signal?: AbortSignal): void => {
			const anchor: HTMLAnchorElement = mentionTarget.element;

			const map: Map<HTMLAnchorElement, SetTimeoutHandle> = timeoutMapRef.current;
			let timeoutHandle: SetTimeoutHandle | undefined = map.get(anchor);

			if (isNil(timeoutHandle)) {
				timeoutHandle = setTimeout(
					() => {
						callback(mentionTarget);
						map.delete(anchor);
					},
					delay
				);

				map.set(anchor, timeoutHandle);
			}

			signal?.addEventListener(
				"abort",
				(): void => {
					clearTimeout(timeoutHandle);
				}
			);
		},
		[callback, delay]
	);

	const cancel = useCallback(
		(anchor: HTMLAnchorElement): void => {
			const map: Map<HTMLAnchorElement, SetTimeoutHandle> = timeoutMapRef.current;
			const timeoutHandle: SetTimeoutHandle | undefined = map.get(anchor);

			if (isNotNil(timeoutHandle)) {
				clearTimeout(timeoutHandle);

				map.delete(anchor);
			}
		},
		[]
	);

	useEffect(
		(): EffectWithDestructorResult => {
			return () => {
				const map: Map<HTMLAnchorElement, SetTimeoutHandle> = timeoutMapRef.current;

				for (const value of map.values()) {
					clearTimeout(value);
				}

				map.clear();
			};
		},
		[cancel]
	);

	return [trigger, cancel];
}
